// src/Pages/ContactUs.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from 'lucide-react';

const ContactUs: React.FC = () => {
  return (
    <div className="bg-white font-[Urbanist] overflow-hidden">
      
      {/* 1. HERO SECTION */}
      <section className="relative py-20 md:py-28 bg-slate-900 text-white">
        <div className="container mx-auto px-6 max-w-7xl relative z-10 text-center space-y-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight"
          >
            Get in <span className="text-blue-500">Touch</span>
          </motion.h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto">
            Have questions about Motilal Algo or need technical support? Our team is here to help you trade better.
          </p>
        </div>
        
        {/* Decorative background grid */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      </section>

      {/* 2. CONTACT INFO & FORM SECTION */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col lg:flex-row gap-16">
            
            {/* LEFT: Contact Information */}
            <div className="lg:w-1/3 space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-extrabold text-slate-900">Contact Information</h2>
                <p className="text-slate-600 font-medium">Fill out the form and our team will get back to you within 24 hours.</p>
              </div>

              <div className="space-y-6">
                {/* Phone Card */}
                <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Call Us</p>
                    <p className="text-lg font-bold text-slate-900">+91 75628 71226</p>
                  </div>
                </div>

                {/* Email Card */}
                <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Email Support</p>
                    <p className="text-lg font-bold text-slate-900">support@motilalalgo.com</p>
                  </div>
                </div>

                {/* Address Card */}
                <div className="flex items-start gap-5 p-6 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Our Office</p>
                    <p className="text-lg font-bold text-slate-900">Level 11, Canberra Block, UB City, 24 Vittal Mallya Road, Bangalore - 560001.</p>
                  </div>
                </div>

                {/* Support Hours */}
                <div className="flex items-start gap-5 p-6 bg-blue-50/50 rounded-2xl border border-blue-100">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-600/20">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-blue-600 uppercase tracking-wider">Support Hours</p>
                    <p className="text-slate-700 font-bold">Mon - Sat: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:w-2/3 bg-white border border-slate-200 p-8 md:p-12 rounded-[2rem] shadow-2xl shadow-slate-200/50"
            >
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Full Name</label>
                  <input type="text" placeholder="John Doe" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Email Address</label>
                  <input type="email" placeholder="john@example.com" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Subject</label>
                  <input type="text" placeholder="How can we help you?" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">Your Message</label>
                  <textarea rows={5} placeholder="Write your message here..." className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium resize-none"></textarea>
                </div>
                <div className="md:col-span-2 pt-4">
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-5 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-blue-600/20 transition-all active:scale-95">
                    <Send size={20} /> Send Message
                  </button>
                </div>
              </form>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 3. QUICK CHAT SECTION */}
      <section className="pb-20">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="bg-slate-900 rounded-[3rem] p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10 overflow-hidden relative">
             {/* Decorative glows */}
             <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[100px] -mr-32 -mt-32" />
             
             <div className="relative z-10 space-y-4 text-center md:text-left">
               <h3 className="text-3xl md:text-4xl font-extrabold text-white">Need a quick answer?</h3>
               <p className="text-slate-400 text-lg font-medium">Join our community or talk to a live expert right now.</p>
             </div>
             
             <div className="relative z-10 flex flex-col sm:flex-row gap-4">
               <button className="px-8 py-4 bg-blue-600 text-white font-extrabold rounded-xl hover:bg-blue-500 transition-all flex items-center justify-center gap-2">
                 <MessageSquare size={20} /> Live Chat
               </button>
               <button className="px-8 py-4 bg-white/10 text-white font-extrabold border border-white/10 rounded-xl hover:bg-white/20 transition-all backdrop-blur-md">
                 Join Discord
               </button>
             </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactUs;